import React, {useState} from 'react';
import {Button, Icon, Modal, Spin, TextArea} from '@gravity-ui/uikit';
import {ArrowDownToLine, Camera, Check, Filmstrip, Pencil, Play, Plus, TrashBin, Xmark} from '@gravity-ui/icons';
import styles from './MediaPoolGallery.module.scss';

export type MediaPoolKind = 'image' | 'video';

export interface MediaPoolItem {
  id: string;
  kind: MediaPoolKind;
  url: string;
  thumbnailUrl?: string | null;
  caption?: string | null;
  fileName?: string;
}

interface MediaPoolGalleryProps {
  items: MediaPoolItem[];
  // Если editable не передан — галерея работает в режиме «только просмотр» (read-only):
  // без оверлея действий, плитки «Добавить…» и редактора подписи. Клик по превью
  // в обоих режимах открывает всплывающий попап-просмотрщик.
  editable?: boolean;
  editingId?: string | null;
  editingValue?: string;
  onEditingValueChange?: (value: string) => void;
  onCaptionEditStart?: (item: MediaPoolItem) => void;
  onCaptionSave?: () => void;
  onCaptionCancel?: () => void;
  isCaptionSaving?: boolean;
  onDelete?: (item: MediaPoolItem) => void;
  onAdd?: () => void;
  addHint?: string;
  // Скачивание материала. Рендерится, только если передан onDownload — и в оверлее
  // карточки, и в футере просмотрщика. downloadingId === item.id включает индикатор загрузки.
  onDownload?: (item: MediaPoolItem) => void;
  downloadingId?: string | null;
  // Режим выбора (для сборки истории из материалов). Независим от editable: круглый чекбокс
  // в углу карточки, клик по нему переключает выбор и не открывает просмотрщик.
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
}

const AddTile: React.FC<{onAdd: () => void; addHint?: string}> = ({onAdd, addHint}) => (
  <button type="button" className={styles.addTile} onClick={onAdd}>
    <Icon data={Plus} size={26} />
    <span>Добавить…</span>
    {addHint && <span className={styles.addHint}>{addHint}</span>}
  </button>
);

export const MediaPoolGallery: React.FC<MediaPoolGalleryProps> = ({
  items,
  editable = false,
  editingId = null,
  editingValue = '',
  onEditingValueChange,
  onCaptionEditStart,
  onCaptionSave,
  onCaptionCancel,
  isCaptionSaving = false,
  onDelete,
  onAdd,
  addHint,
  onDownload,
  downloadingId = null,
  selectable = false,
  selectedIds,
  onToggleSelect,
}) => {
  const [viewerItem, setViewerItem] = useState<MediaPoolItem | null>(null);

  // Ctrl+Enter / Cmd+Enter — подтверждение редактирования подписи.
  const handleCaptionKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onCaptionSave?.();
    }
  };

  return (
    <>
      <div className={styles.gallery} data-testid="media-pool-gallery">
        {items.map((item) => {
          const isVideo = item.kind === 'video';
          const isEditing = editable && editingId === item.id;
          const isSelected = selectable && !!selectedIds?.has(item.id);
          return (
            <div
              key={item.id}
              className={`${styles.card} ${isEditing ? styles.cardEditing : ''} ${
                isSelected ? styles.cardSelected : ''
              }`}>
              <div className={styles.media}>
                <span className={styles.kind}>
                  <Icon data={isVideo ? Filmstrip : Camera} size={11} />
                  {isVideo ? 'Видео' : 'Фото'}
                </span>
                {selectable && (
                  <button
                    type="button"
                    className={`${styles.selectBox} ${isSelected ? styles.selectBoxChecked : ''}`}
                    aria-pressed={isSelected}
                    aria-label={isSelected ? 'Снять выбор' : 'Выбрать материал'}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSelect?.(item.id);
                    }}>
                    {isSelected && <Icon data={Check} size={14} />}
                  </button>
                )}
                <button
                  type="button"
                  className={styles.mediaTrigger}
                  onClick={() => setViewerItem(item)}
                  aria-label={isVideo ? 'Смотреть видео' : 'Смотреть фото'}
                >
                  {isVideo ? (
                    item.thumbnailUrl ? (
                      <img
                        data-testid="media-video"
                        className={styles.mediaImg}
                        src={item.thumbnailUrl}
                        alt={item.caption ?? item.fileName ?? ''}
                      />
                    ) : (
                      <span data-testid="media-video" className={styles.mediaPlaceholder}>
                        <Icon data={Filmstrip} size={32} />
                      </span>
                    )
                  ) : (
                    <img
                      data-testid="media-image"
                      className={styles.mediaImg}
                      src={item.url}
                      alt={item.caption ?? item.fileName ?? ''}
                    />
                  )}
                  {isVideo && (
                    <span className={styles.playBadge}>
                      <Icon data={Play} size={22} />
                    </span>
                  )}
                </button>
                {editable && !isEditing && (
                  <div className={styles.overlay}>
                    <button type="button" className={styles.act} onClick={() => onCaptionEditStart?.(item)}>
                      <Icon data={Pencil} size={13} />
                      Изменить подпись
                    </button>
                    {onDownload && (
                      <button
                        type="button"
                        className={styles.act}
                        disabled={downloadingId === item.id}
                        onClick={() => onDownload(item)}
                      >
                        {downloadingId === item.id ? <Spin size="xs" /> : <Icon data={ArrowDownToLine} size={13} />}
                        Скачать
                      </button>
                    )}
                    <button
                      type="button"
                      className={`${styles.act} ${styles.actDanger}`}
                      onClick={() => onDelete?.(item)}
                    >
                      <Icon data={TrashBin} size={13} />
                      Удалить
                    </button>
                  </div>
                )}
              </div>

              {editable ? (
                isEditing ? (
                  <div className={styles.captionForm}>
                    <TextArea
                      value={editingValue}
                      onUpdate={onEditingValueChange}
                      onKeyDown={handleCaptionKeyDown}
                      placeholder="Подпись к материалу"
                      minRows={2}
                      autoFocus
                    />
                    <div className={styles.captionActions}>
                      <Button size="s" view="flat" onClick={onCaptionCancel} disabled={isCaptionSaving}>
                        Отмена
                      </Button>
                      <Button size="s" view="action" onClick={onCaptionSave} loading={isCaptionSaving}>
                        Сохранить
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.caption}>
                    <button
                      type="button"
                      className={`${styles.captionText} ${item.caption ? '' : styles.captionEmpty}`}
                      onClick={() => onCaptionEditStart?.(item)}
                    >
                      {item.caption || 'Добавить подпись'}
                    </button>
                    <button
                      type="button"
                      className={styles.captionEdit}
                      title="Изменить подпись"
                      onClick={() => onCaptionEditStart?.(item)}
                    >
                      <Icon data={Pencil} size={13} />
                    </button>
                  </div>
                )
              ) : (
                item.caption && (
                  <div className={styles.caption}>
                    <span className={styles.captionText}>{item.caption}</span>
                  </div>
                )
              )}
            </div>
          );
        })}

        {editable && onAdd && <AddTile onAdd={onAdd} addHint={addHint} />}
      </div>

      <Modal
        open={!!viewerItem}
        onOpenChange={(open) => {
          if (!open) setViewerItem(null);
        }}
        className={styles.viewerModal}
      >
        {viewerItem && (
          <div className={styles.viewer}>
            <button
              type="button"
              className={styles.viewerClose}
              onClick={() => setViewerItem(null)}
              aria-label="Закрыть"
            >
              <Icon data={Xmark} size={18} />
            </button>
            <div className={styles.viewerStage}>
              {viewerItem.kind === 'video' ? (
                <video
                  data-testid="viewer-video"
                  className={styles.viewerVideo}
                  src={viewerItem.url}
                  poster={viewerItem.thumbnailUrl ?? undefined}
                  controls
                  autoPlay
                  playsInline
                />
              ) : (
                <img
                  data-testid="viewer-image"
                  className={styles.viewerImg}
                  src={viewerItem.url}
                  alt={viewerItem.caption ?? viewerItem.fileName ?? ''}
                />
              )}
            </div>
            {(viewerItem.caption || onDownload) && (
              <div className={styles.viewerFooter}>
                {viewerItem.caption && <div className={styles.viewerCaption}>{viewerItem.caption}</div>}
                {onDownload && (
                  <button
                    type="button"
                    className={styles.viewerDownload}
                    disabled={downloadingId === viewerItem.id}
                    onClick={() => onDownload(viewerItem)}
                  >
                    {downloadingId === viewerItem.id ? (
                      <Spin size="xs" />
                    ) : (
                      <Icon data={ArrowDownToLine} size={16} />
                    )}
                    Скачать
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};
